import { defineStore } from 'pinia'
import { ref } from 'vue'
import client from '../api/client.js'

/**
 * Pinia store for Tontine OS operations.
 * 
 * Manages tontine plan state, cycles, contributions, and payouts.
 * 
 * Avocash positioning: This store coordinates tontine operations in the UI.
 * Licensed financial partners handle payment execution and safeguarding where required.
 */
export const useTontineStore = defineStore('tontine', () => {
  const plan = ref(null)
  const cycles = ref([])
  const cycleDetail = ref(null)
  const payouts = ref([])
  const loading = ref(false)
  const error = ref(null)

  /**
   * Load a tontine plan by campaign ID.
   * 
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<Object>} The tontine plan
   */
  async function load(campaignId) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.get(`/campaigns/${campaignId}/tontine`)
      plan.value = data
      return data
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Load the enriched plan summary for the tontine home screen.
   * 
   * Includes campaign name, member counts, the caller's role and permissions,
   * readiness blockers, the current cycle and the caller's contribution.
   * 
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<Object>} The plan summary
   */
  async function loadSummary(campaignId) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.get(`/campaigns/${campaignId}/tontine/summary`)
      plan.value = data
      return data
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Load a single cycle detail (cycle + visibility-filtered contributions + payout).
   * 
   * @param {string} campaignId - The campaign ID
   * @param {string} cycleId - The cycle ID
   * @returns {Promise<Object>} The cycle detail response
   */
  async function fetchCycleDetail(campaignId, cycleId) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.get(`/campaigns/${campaignId}/tontine/cycles/${cycleId}`)
      cycleDetail.value = data
      return data
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Load all payout instructions for the plan, newest first.
   * 
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<Array>} The payout instructions
   */
  async function fetchPayouts(campaignId) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.get(`/campaigns/${campaignId}/tontine/payouts`)
      payouts.value = data
      return data
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Create a new tontine plan for a campaign.
   * 
   * @param {string} campaignId - The campaign ID
   * @param {Object} payload - The plan creation request
   * @returns {Promise<Object>} The created tontine plan
   */
  async function create(campaignId, payload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.post(`/campaigns/${campaignId}/tontine`, payload)
      plan.value = data
      return data
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Activate a tontine plan.
   * 
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<Object>} The activated tontine plan
   */
  async function activate(campaignId) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.post(`/campaigns/${campaignId}/tontine/activate`)
      plan.value = data
      return data
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Generate cycles for a tontine plan (fixed rotation).
   * 
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<Array>} The generated cycles
   */
  async function generateCycles(campaignId) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.post(`/campaigns/${campaignId}/tontine/cycles/generate`)
      cycles.value = data
      if (plan.value) {
        plan.value.cycles = data
      }
      return data
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Get all cycles for a tontine plan.
   * 
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<Array>} The cycles
   */
  async function fetchCycles(campaignId) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.get(`/campaigns/${campaignId}/tontine/cycles`)
      cycles.value = data
      return data
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Submit contribution evidence (receipt).
   * 
   * Used in COORDINATION_ONLY mode where members upload receipts
   * for treasurer verification.
   * 
   * @param {string} campaignId - The campaign ID
   * @param {string} contributionId - The contribution ID
   * @param {Object} payload - The evidence submission request
   * @returns {Promise<Object>} The updated contribution
   */
  async function submitReceipt(campaignId, contributionId, payload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.post(
        `/campaigns/${campaignId}/tontine/contributions/${contributionId}/receipt`,
        payload
      )
      return data
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Confirm a contribution.
   * 
   * Used by treasurer or finance admin to verify member payments.
   * 
   * @param {string} campaignId - The campaign ID
   * @param {string} contributionId - The contribution ID
   * @param {Object} payload - The confirmation request
   * @returns {Promise<Object>} The confirmed contribution
   */
  async function confirmContribution(campaignId, contributionId, payload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.post(
        `/campaigns/${campaignId}/tontine/contributions/${contributionId}/confirm`,
        payload
      )
      return data
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Prepare a payout instruction for a cycle.
   * 
   * Creates a payout instruction requiring dual approval before execution.
   * 
   * @param {string} campaignId - The campaign ID
   * @param {string} cycleId - The cycle ID
   * @param {Object} payload - The payout preparation request
   * @returns {Promise<Object>} The prepared payout instruction
   */
  async function preparePayout(campaignId, cycleId, payload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.post(
        `/campaigns/${campaignId}/tontine/cycles/${cycleId}/payouts`,
        payload
      )
      return data
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Approve or reject a payout instruction.
   * 
   * Dual approval workflow: requires multiple approvers to grant approval.
   * 
   * @param {string} campaignId - The campaign ID
   * @param {string} payoutId - The payout instruction ID
   * @param {Object} payload - The decision request
   * @returns {Promise<Object>} The updated payout instruction
   */
  async function decidePayout(campaignId, payoutId, payload) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.post(
        `/campaigns/${campaignId}/tontine/payouts/${payoutId}/decision`,
        payload
      )
      return data
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Get plan readiness assessment.
   * 
   * @param {string} campaignId - The campaign ID
   * @returns {Promise<Object>} Readiness assessment with blockers if not ready
   */
  async function getReadiness(campaignId) {
    loading.value = true
    error.value = null
    try {
      const { data } = await client.get(`/campaigns/${campaignId}/tontine/readiness`)
      return data
    } catch (err) {
      error.value = err
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Clear the current plan state.
   */
  function clearPlan() {
    plan.value = null
    cycles.value = []
    cycleDetail.value = null
    payouts.value = []
    error.value = null
  }

  return {
    plan,
    cycles,
    cycleDetail,
    payouts,
    loading,
    error,
    load,
    loadSummary,
    create,
    activate,
    generateCycles,
    fetchCycles,
    fetchCycleDetail,
    fetchPayouts,
    submitReceipt,
    confirmContribution,
    preparePayout,
    decidePayout,
    getReadiness,
    clearPlan,
  }
})
